import { auth } from "@/main";
import {
  sendEmailVerification,
  sendPasswordResetEmail,
  applyActionCode,
  getIdTokenResult,
  createUserWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import Oob from "@/utils/oobCode";
import { AuthMessageEnum } from "@/enums/index";

const FRONTEND_ORIGIN =
  process.env.VUE_APP_FRONTEND_ORIGIN || window.location.origin;
const VERIFICATION_EXPIRATION_MS = Number(
  process.env.VUE_APP_VERIFICATION_EXPIRATION_MS || 600000
); // 10 min
const RETRY_VERIFICATION_EMAIL_MS = Number(
  process.env.VUE_APP_RETRY_VERIFICATION_EMAIL_MS || 180000
); // 3 min

const verificationACS = {
  url: `${FRONTEND_ORIGIN}/verify-email`,
  handleCodeInApp: true,
};
const resetACS = {
  url: `${FRONTEND_ORIGIN}/reset-password`,
  handleCodeInApp: true,
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms)); // optional delay

const Auth = {
  oobcode: {
    status: async () => {
      const last = Oob.getVerificationLastSentAt();
      return Oob.buildStatus(
        last,
        VERIFICATION_EXPIRATION_MS,
        RETRY_VERIFICATION_EMAIL_MS
      );
    },
    use: async (oobCode) => {
      if (!oobCode) {
        const err = new Error(AuthMessageEnum.MISSING_OOBCODE);
        err.status = 400;
        throw err;
      }
      await applyActionCode(auth, oobCode);
      if (auth.currentUser) {
        await auth.currentUser.reload();
        await getIdTokenResult(auth.currentUser, true);
      }
      Oob.clearVerificationLastSentAt();
      return { message: AuthMessageEnum.USER_VERIFIED };
    },
  },

  // -------------------------------
  // SIGN UP (neutralizes email leaks)
  // -------------------------------
  signup: async ({ firstName = "", lastName = "", email, password }) => {
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      const displayName = `${firstName} ${lastName}`.trim();
      if (displayName) await updateProfile(user, { displayName });

      try {
        await sendEmailVerification(user, verificationACS);
      } catch (e) {
        if (e?.code === "auth/too-many-requests") {
          console.warn("[SECURITY] Verification throttled:", e.code);
        } else {
          console.warn("[SECURITY] Verification email error:", e?.code || e);
        }
      }

      Oob.markVerificationSentNow();
      await user.getIdToken(true);
      return { user, message: AuthMessageEnum.VERIFICATION_SENT };
    } catch (error) {
      // Prevent user enumeration
      if (
        error.code === "auth/email-already-in-use" ||
        error.code === "auth/user-not-found" ||
        error.code === "auth/too-many-requests"
      ) {
        console.warn("[SECURITY] Enumeration-safe suppression:", error.code, email);
        try {
          await sendPasswordResetEmail(auth, email, resetACS);
        } catch (_) {
          /* ignore secondary throttles */
        }
        await wait(500 + Math.random() * 200);
        return { message: AuthMessageEnum.GENERIC_INSTRUCTIONS };
      }

      throw error; // still show legit errors (weak-password, invalid-email, etc.)
    }
  },

  // -------------------------------
  // PASSWORD RESET (neutralizes email leaks)
  // -------------------------------
  forgotPassword: async (email) => {
    if (!email) {
      const err = new Error("Email is required");
      err.status = 400;
      throw err;
    }

    localStorage.setItem("last_reset_email", email);

    try {
      await sendPasswordResetEmail(auth, email, resetACS);
    } catch (error) {
      if (
        error.code === "auth/user-not-found" ||
        error.code === "auth/too-many-requests"
      ) {
        console.warn("[SECURITY] Enumeration-safe suppression:", error.code, email);
      } else {
        throw error;
      }
    }

    await wait(500 + Math.random() * 200);
    Oob.markResetSentNow();
    return { message: AuthMessageEnum.GENERIC_RESET };
  },

  sendVerificationEmail: async () => {
    if (!auth.currentUser) {
      const err = new Error(AuthMessageEnum.NOT_SIGNED_IN);
      err.status = 401;
      throw err;
    }
    await sendEmailVerification(auth.currentUser, verificationACS);
    Oob.markVerificationSentNow();
    return { message: AuthMessageEnum.VERIFICATION_SENT };
  },
};

export default Auth;
